import * as React from 'react';
import { PriceDynamicsFilters } from '../../components/PriceDynamicsFilters/PriceDynamicsFilters';
import { EChartTypes, ERoomsCount, ValuationChartsWidget } from '@cian/valuation-web-charts-widget';
import { useApplicationContext } from '../../utils/applicationContext';
import { useDispatch, useSelector } from 'react-redux';
import { ChartInfo } from '../../components/ChartInfo';
import { today } from '../../utils/getTodayDate';
import { useGetMinDate } from './utils/useGetMinDate';
import { PriceDynamicsInfo } from '../../components/PriceDynamicsInfo';
import { useGetDiff } from './utils/useGetDiff';
import { useIsDesktop, useIsFirstRun } from '@cian/valuation-utils-component';
import { trackPeriodFilterChange, trackRoomsFilterChange } from '../MarketTrendsContainer/utils/tracking';
import { trackOnChartHover, trackPriceDynamicExist } from './tracking';
import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import { trackPriceDynamicView } from '../../utils/teaser';
import { VIEWED_TIMEOUT } from '../../constants/viewedBlock';
import { useGetPeriodText } from './utils/useGetPeriodText';
import { EGetInfo, useGetInfo } from './utils/useGetInfo';
import { PriceDynamicWrapper } from '../../components/PriceDynamicWrapper';
import { TrendsSpinner } from '../../components/TrendsSpinner';
import { getMarketTrend, setPeriodInFilter, setRoomsCountInFilter } from '../../actions/marketTrends';
import {
  selectAvailableFilters,
  selectIsMarketTrandLoading,
  selectMarketTrandData,
  selectPeriod,
  selectRoomsCount,
} from '../../selectors/marketTrend';
import { TThunkDispatch } from '../../types/redux';
import { EPeriod } from '../../repositories/valuation-offer-history/v1/get-market-trand';

export function PriceDynamicsContainer() {
  const { logger } = useApplicationContext();

  const wrapperRef = React.useRef(null);
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch<TThunkDispatch>();
  const isFirstRun = useIsFirstRun();

  const roomsCount = useSelector(selectRoomsCount);
  const period = useSelector(selectPeriod);

  const filters = useSelector(selectAvailableFilters);

  const {
    marketTrand: marketTrend,
    houseTrand: houseTrend,
    diffPriceTrand,
    averagePrice,
    averageSqmPrice,
    diffPrice,
    diffPercent,
  } = useSelector(selectMarketTrandData);
  const isLoading = useSelector(selectIsMarketTrandLoading);

  React.useEffect(() => {
    if (!isFirstRun) {
      dispatch(getMarketTrend());
    }
  }, [period, roomsCount]);

  React.useEffect(() => {
    trackPriceDynamicExist();
  }, []);

  const onRoomsCountChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;

      dispatch(setRoomsCountInFilter(value as ERoomsCount));
      trackRoomsFilterChange(value as ERoomsCount);
    },
    [dispatch, roomsCount],
  );

  const onPeriodChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.currentTarget.value;

      dispatch(setPeriodInFilter(value as EPeriod));
      trackPeriodFilterChange(value as EPeriod);
    },
    [dispatch, period],
  );

  const dateMin = useGetMinDate(period);
  const differencePrice = useGetDiff({ diffPriceTrand, diffPrice, diffPercent });
  const isRoomsCountAll = roomsCount === ERoomsCount.All;
  const periodText = useGetPeriodText(period);
  const averageContent = useGetInfo({
    isLoading,
    averageSqmPrice,
    isRoomsCountAll,
    averagePrice,
    type: EGetInfo.PriceAverage,
  });
  const priceChangeContent = useGetInfo({
    isLoading,
    isRoomsCountAll,
    type: EGetInfo.PriceChange,
    differencePrice,
  });

  const chartInfoText = {
    magentaText: houseTrend.length > 0 ? 'Этот дом' : null,
    blueText: marketTrend.length > 0 ? 'Соседние дома' : null,
  };

  const chartKey = `${diffPrice}${diffPercent}${diffPriceTrand}${marketTrend[0]?.value}${houseTrend[0]?.value}`;

  return (
    <ActionAfterViewObserver callback={trackPriceDynamicView} callbackDelay={VIEWED_TIMEOUT} wrapper={false}>
      <PriceDynamicWrapper ref={wrapperRef}>
        <PriceDynamicsFilters
          filters={filters}
          roomsCount={roomsCount}
          onRoomsCountChange={onRoomsCountChange}
          period={period}
          onPeriodChange={onPeriodChange}
          isDesktop={isDesktop}
        />
        <TrendsSpinner isLoading={isLoading} />
        <PriceDynamicsInfo
          differencePrice={differencePrice}
          isRoomsCountAll={isRoomsCountAll}
          periodText={periodText}
          averageContent={averageContent}
          priceChangeContent={priceChangeContent}
        />
        <ChartInfo {...chartInfoText} />
        <ValuationChartsWidget
          key={chartKey}
          data={{
            dateMin,
            dateMax: today(),
            xScale: 'month',
            marketTrend,
            houseTrend,
          }}
          activeFilter={roomsCount}
          type={EChartTypes.Trends}
          logger={logger}
          onHover={trackOnChartHover}
        />
      </PriceDynamicWrapper>
    </ActionAfterViewObserver>
  );
}
